import { priorityWeight } from '@/components/deal/priority/priority-types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import FindInViewFilter from '@/components/ui/find-in-view-filter/find-in-view-filter';
import { useFindInViewFilterContext } from '@/components/ui/find-in-view-filter/find-in-view-filter-provider';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HeaderWithSidebarToggle from '@/components/ui/layout/header-with-sidebar-toggle';
import { PDKListMinusIcon, PDKMenuIcon } from '@/components/ui/pdk-icons';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useDealContext } from '@/contexts/deal-provider';
import { useNotificationsContext } from '@/contexts/notifications-provider';
import { useBulkDeleteNotifications } from '@/hooks/use-bulk-delete-notifications';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import { useDeleteNotificationWithUndo } from '@/hooks/use-delete-notification-with-undo';
import useLocalStorage from '@/hooks/use-local-storage';
import { useIsMobile } from '@/hooks/use-mobile.tsx';
import { getInformativeDealTitle } from '@/hooks/use-redirect-to-informative-deal-url';
import useSessionStorage from '@/hooks/use-session-storage';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import isElementInViewport from '@/lib/is-element-in-viewport';
import { pdkAxios } from '@/lib/pdk-axios';
import { deferToNextFrame } from '@/lib/utils.ts';
import type { Notification } from '@/models/registry';
import { notificationStore } from '@/models/stores/index';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { ListFilter, SlidersHorizontal } from 'lucide-react';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useInboxContext } from '../../../contexts/inbox-provider';
import { BulkDeleteDialog, type DialogCategory } from './bulk-delete-dialog';
import VirtualizedInboxList from './virtualized-inbox-list';

const INBOX_SCROLL_POSITION_KEY = 'inbox-scroll-position';

type OrderingOption = 'Newest' | 'Oldest' | 'Priority';

export const InboxLayout: FC<{ aside?: ReactNode }> = ({ aside }) => {
  const { setIsInboxView } = useInboxContext();
  useEffect(() => {
    // UX NOTE: whenever we open the inbox ... we set the inbox view session state!
    setIsInboxView(true);
  }, []);

  const [scrollPos, setScrollPos] = useSessionStorage(
    INBOX_SCROLL_POSITION_KEY,
    0,
  );

  const debouncedSetScrollPos = useCallback(
    debounce((pos: number) => {
      setScrollPos(pos);
    }, 150),
    [],
  );

  const inboxListRef = useRef<HTMLDivElement>(null);

  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const calcIsCurrentDeal = useCallback(
    (dealShortId: string) => dealShortId === deal?.short_id,
    [deal?.short_id],
  );

  const getContainerId = useCallback(
    (dealId: string) => `inbox-line-item-container-${dealId}`,
    [],
  );

  useEffect(() => {
    // UX NOTE: keep the same scroll position inbetween new deal page loads
    if (inboxListRef.current) {
      inboxListRef.current.scrollTop = scrollPos;
    }
  }, []);

  useEffect(() => {
    if (!deal) return;

    const currentInboxLineItemContainer = document.getElementById(
      getContainerId(deal.id),
    );
    // UX NOTE: if the element isn't viewport, we should show it at the bottom
    if (
      currentInboxLineItemContainer &&
      !isElementInViewport(currentInboxLineItemContainer)
    ) {
      currentInboxLineItemContainer.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [deal?.id]);

  const { findInViewQuery } = useFindInViewFilterContext();

  const { notifications: unfilteredNotifications } = useNotificationsContext();
  const deleteNotification = useDeleteNotificationWithUndo();
  const {
    deleteReadNotifications,
    deleteDealCancelledNotifications,
    deleteDealDoneNotifications,
  } = useBulkDeleteNotifications({ notifications: unfilteredNotifications });

  const [bulkDialogIsOpen, setBulkDialogIsOpen] = useState(false);
  const [category, setCategory] = useState<DialogCategory>('read');

  const openBulk = (c: DialogCategory) => {
    setCategory(c);
    setBulkDialogIsOpen(true);
  };

  const [inboxShowRead, setInboxShowRead] = useLocalStorage<boolean>(
    'inbox-show-read',
    true,
  );
  const [ordering, setOrdering] = useLocalStorage<OrderingOption>(
    'inbox-ordering',
    'Newest',
  );

  const notifications = useMemo(() => {
    const searchQuery = findInViewQuery.toLowerCase();
    const filtered = unfilteredNotifications
      .filter((notification) => {
        const unread = !notification.isRead;
        return inboxShowRead || unread;
      })
      .filter((notification) => {
        // todo: fix this
        const dealShortId = notification.deal?.short_id?.toLowerCase() ?? '';
        // const dealShortId = 'SFO-100';
        const userFullName =
          `${notification.user?.first_name} ${notification.user?.last_name}`.toLowerCase();
        const deal = notification.deal;
        const vin = deal?.vehicle_listing?.vehicle?.vin;

        const title =
          (deal?.primary_customer &&
            deal?.vehicle_listing &&
            getInformativeDealTitle({
              primaryCustomerFullName: deal?.primary_customer.full_name,
              vehicle: deal.vehicle_listing,
              primarySalesRep: deal.primary_sales_rep,
              secondarySalesRep: deal.secondary_sales_rep,
            })) ||
          '';

        return (
          dealShortId.includes(searchQuery) ||
          notification.message.toLowerCase().includes(searchQuery) ||
          userFullName.includes(searchQuery) ||
          title.toLowerCase().includes(searchQuery) ||
          (vin ? vin.toLowerCase().includes(searchQuery) : false)
        );
      });

    const sorted = [...filtered].sort((a, b) => {
      if (ordering === 'Newest') {
        const newOrder =
          dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf();
        return newOrder;
      } else if (ordering === 'Oldest') {
        const oldOrder =
          dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf();
        return oldOrder;
      } else if (ordering === 'Priority') {
        const aPriority = a.deal?.priority ?? 'none';
        const bPriority = b.deal?.priority ?? 'none';
        const priorityOrder =
          priorityWeight[bPriority] - priorityWeight[aPriority];
        return priorityOrder;
      }
      return 0;
    });

    return sorted;
  }, [unfilteredNotifications, findInViewQuery, inboxShowRead, ordering]);

  const markAsRead = useMutation({
    onMutate: async ({ notification }: { notification: Notification }) => {
      notificationStore.markAsRead(notification.id);
    },
    mutationFn: async ({ notification }: { notification: Notification }) => {
      const response = await pdkAxios.post(
        `/notifications/${notification.id}/read`,
      );
      return response.data;
    },

    onSuccess: () => {
      // UX NOTE: we don't want to invalidate the query here because we want
      // to keep the order of the notifications
    },

    onError: (error) => {
      console.error('Error marking as read', error);
    },
  });

  const markAsUnread = useMutation({
    onMutate: async ({ notification }: { notification: Notification }) => {
      notificationStore.markAsUnread(notification.id);
    },
    mutationFn: async ({ notification }: { notification: Notification }) => {
      const response = await pdkAxios.post(
        `/notifications/${notification.id}/unread`,
      );
      return response.data;
    },

    onSuccess: () => {
      // UX NOTE: we don't want to invalidate the query here because we want
      // to keep the order of the notifications
    },

    onError: (error) => {
      console.error('Error marking as unread', error);
    },
  });

  const handleMarkAsRead = useCallback(
    (notification: Notification) => markAsRead.mutate({ notification }),
    [markAsRead.mutate],
  );

  const handleMarkAsUnread = useCallback(
    (notification: Notification) => markAsUnread.mutate({ notification }),
    [markAsUnread.mutate],
  );

  const handleDeleteNotification = useCallback(
    (notification: Notification) => deleteNotification.mutate({ notification }),
    [deleteNotification.mutate],
  );

  const activeNotification = useMemo(() => {
    if (!deal) return null;

    return notifications.find(
      (notification) => notification.deal_id === deal.id,
    );
  }, [deal?.id, notifications]);

  useEffect(() => {
    if (!activeNotification) return;

    if (activeNotification && !activeNotification.isRead) {
      markAsRead.mutate({ notification: activeNotification });
    }
  }, [activeNotification?.id]);

  useHotkeys(HOTKEYS.TOGGLE_READ_UNREAD, () => {
    if (!activeNotification) return;

    if (!activeNotification.isRead) {
      markAsRead.mutate({ notification: activeNotification });
    }

    if (activeNotification.isRead) {
      markAsUnread.mutate({ notification: activeNotification });
    }
  });

  useHotkeys(
    HOTKEYS.DELETE_SINGLE_NOTIFICATION,
    () => {
      if (!activeNotification) return;

      deleteNotification.mutate({ notification: activeNotification });
    },
    [activeNotification, deleteNotification],
  );

  useHotkeys(
    HOTKEYS.DELETE_ALL_READ_NOTIFICATION,
    () => {
      deferToNextFrame(() => openBulk('read'));
    },
    [deferToNextFrame, openBulk],
  );

  const { showFindInView } = useFindInViewFilterContext();

  const isMobile = useIsMobile();

  return (
    <div
      className="flex flex-row max-h-screen w-full dark:bg-dark"
      id="inbox-layout"
    >
      <div
        className={`${isMobile ? 'w-full' : 'w-96'} border-r max-h-screen overflow-y-auto h-screen`}
        ref={inboxListRef}
        onScroll={(e) => {
          debouncedSetScrollPos(e.currentTarget.scrollTop);
        }}
      >
        <div className="border-b">
          <HeaderWithSidebarToggle>
            <div className="flex flex-row items-center px-2 py-2 gap-2 sticky top-0 z-20 bg-white dark:bg-dark justify-between w-full h-10">
              <div className="flex flex-row gap-2 items-center">
                <div>Inbox</div>
                <div className="flex flex-row gap-2 items-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button>
                        <PDKMenuIcon className="size-4  cursor-pointer" />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="start" className="w-80">
                      <DropdownMenuItem
                        onSelect={() =>
                          deferToNextFrame(() => openBulk('read'))
                        }
                      >
                        <PDKListMinusIcon className="size-4  cursor-pointer" />
                        <span>Delete all read</span>
                        <span className="ml-auto">
                          <KeyboardKeyIcon>
                            {HOTKEYS.DELETE_ALL_READ_NOTIFICATION}
                          </KeyboardKeyIcon>
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          deferToNextFrame(() => openBulk('cancelled'))
                        }
                      >
                        <PDKListMinusIcon className="size-4  cursor-pointer" />
                        <span>Delete all for cancelled deals</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() =>
                          deferToNextFrame(() => openBulk('done'))
                        }
                      >
                        <PDKListMinusIcon className="size-4  cursor-pointer" />
                        <span>Delete all for done deals</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <BulkDeleteDialog
                open={bulkDialogIsOpen}
                onOpenChange={setBulkDialogIsOpen}
                category={category}
                onConfirm={() => {
                  if (category === 'read') {
                    deleteReadNotifications.mutate(undefined, {
                      onSuccess: () => setBulkDialogIsOpen(false),
                    });
                  } else if (category === 'cancelled') {
                    deleteDealCancelledNotifications.mutate(undefined, {
                      onSuccess: () => setBulkDialogIsOpen(false),
                    });
                  } else {
                    deleteDealDoneNotifications.mutate(undefined, {
                      onSuccess: () => setBulkDialogIsOpen(false),
                    });
                  }
                }}
              />

              {!showFindInView && (
                <div className="flex flex-row gap-2 items-center">
                  <ListFilter
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      toast({
                        title: 'Feature not implemented yet',
                        description:
                          "Slack us if you want 'notification filters' feature",
                      });
                    }}
                  />
                  <Popover modal={true}>
                    <PopoverTrigger asChild>
                      <SlidersHorizontal className="size-4 cursor-pointer" />
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[200px] p-0"
                      side="bottom"
                      align="end"
                    >
                      <div className="border rounded-lg shadow-lg p-4 text-sm space-y-2">
                        <div className="flex items-center justify-between w-full space-x-6">
                          <label className="text-black text-sm select-none">
                            Ordering
                          </label>
                          <Select
                            value={ordering}
                            onValueChange={(val: OrderingOption) =>
                              setOrdering(val)
                            }
                          >
                            <SelectTrigger className="h-4">
                              <SelectValue placeholder="Newest" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Newest">Newest</SelectItem>
                              <SelectItem value="Oldest">Oldest</SelectItem>
                              <SelectItem value="Priority">Priority</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between w-full space-x-6">
                          <label
                            className="text-black text-sm select-none"
                            htmlFor="show-read"
                          >
                            {' '}
                            Show read{' '}
                          </label>
                          <Switch
                            id="show-read"
                            checked={inboxShowRead}
                            onCheckedChange={setInboxShowRead}
                          />
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              <FindInViewFilter />
            </div>
          </HeaderWithSidebarToggle>
        </div>
        <VirtualizedInboxList
          notifications={notifications}
          getContainerId={getContainerId}
          calcIsCurrentDeal={calcIsCurrentDeal}
          markAsRead={handleMarkAsRead}
          markAsUnread={handleMarkAsUnread}
          onDeleteNotification={handleDeleteNotification}
          numOfFilteredNotifications={
            unfilteredNotifications.length - notifications.length
          }
        />
      </div>
      {!isMobile && <div className="flex flex-row w-full">{aside}</div>}
    </div>
  );
};

const InboxLayoutWrapper = ({ aside }: { aside: ReactNode }) => {
  return <InboxLayout aside={aside} />;
};

export default InboxLayoutWrapper;
